import React, { useEffect, useState } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/ServiceProvider.css";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Copy, Building2, Users, Edit } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEdit } from "react-icons/fa";
import { FaCalendarAlt } from "react-icons/fa";

const ServiceProvider = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [employees, setEmployees] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const [storedEmployeeImages, setStoredEmployeeImages] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [newResignDate, setNewResignDate] = useState("");

  const societyName = localStorage.getItem("society");
  const societyRegNo = localStorage.getItem("societyRegNo") || "";

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const normalizeToArray = (payload) => {
    if (payload === null || payload === undefined) return [];
    if (Array.isArray(payload)) return payload;

    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.providers)) return payload.providers;
      if (Array.isArray(payload.data)) return payload.data;

      const firstArray = Object.values(payload).find((v) => Array.isArray(v));
      if (firstArray) return firstArray;

      const likelySingle =
        payload.companyName || payload.companyEmail || payload.name || payload.id || payload._id;
      if (likelySingle) return [payload];
    }
    return [];
  };

  const fetchEmployeeImage = async (employeeId, companyName, controllerSignal = undefined) => {
    if (!employeeId) return;
    if (!companyName && !societyName) return;

    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${BASE_URL}/api/employee/image`, {
        params: {
          employeeId,
          societyName: societyName || "",
          companyName: companyName || "",
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controllerSignal,
      });

      if (!res || !res.data) return;
      const { image, contentType } = res.data || {};
      if (image) {
        const dataUrl = `data:${contentType || "image/jpeg"};base64,${image}`;
        setStoredEmployeeImages((prev) => ({ ...prev, [employeeId]: dataUrl }));
      }
    } catch (err) {
      // ignore individual-image failures
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file)); // 👈 preview
    }
  };

  useEffect(() => {
    if (showEditModal) {
      setSelectedFile(null);
      setPreviewImage(null);
    }
  }, [showEditModal]);

  const fetchEmployees = async (companyName, controllerSignal) => {
    if (!companyName) {
      setEmployees([]);
      return;
    }
    const token = localStorage.getItem("token");
    setLoadingEmployees(true);
    try {
      const url = `${BASE_URL}/api/employee/get-emp-by-company-name?companyName=${encodeURIComponent(
        companyName
      )}&societyName=${encodeURIComponent(localStorage.getItem("society") || "")}`;

      const response = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controllerSignal,
      });

      console.log("fetchEmployees response shape:", response.data);
      const arr = normalizeToArray(response.data);
      setEmployees(arr);

      if (Array.isArray(arr) && arr.length > 0) {
        arr.forEach((emp) => {
          if (emp && (emp.employeeId || emp.employeeId === 0)) {
            if (!storedEmployeeImages[emp.employeeId]) {
              fetchEmployeeImage(emp.employeeId, companyName, controllerSignal);
            }
          }
        });
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.message === "canceled") {
        console.log("fetchEmployees request cancelled");
      } else {
        console.error("Error fetching employees", error);
        setEmployees([]);
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    const societyNameLocal = localStorage.getItem("society") || "";
    if (!societyNameLocal || societyNameLocal.trim() === "") {
      console.warn("No society name found in localStorage (key: 'society'). Skipping providers fetch.");
      setProviders([]);
      setSelectedCompany("");
      setEmployees([]);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const token = localStorage.getItem("token");
        const url = `${BASE_URL}/api/services/get-all-services?societyName=${encodeURIComponent(
          societyNameLocal
        )}`;

        console.log("Requesting providers ->", url);
        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });

        console.log("fetchProviders response shape:", response.data);
        const arr = normalizeToArray(response.data);
        setProviders(arr);

        if (arr.length > 0) {
          const first = arr[0];
          const company = first.companyName || "";
          setSelectedCompany(company);
          if (company) {
            fetchEmployees(company, signal);
          } else {
            setEmployees([]);
          }
        } else {
          setSelectedCompany("");
          setEmployees([]);
        }
      } catch (error) {
        if (error.name === "CanceledError" || error.message === "canceled") {
          console.log("fetchProviders cancelled");
        } else {
          console.error("Failed to fetch providers", error);
          setProviders([]);
          setSelectedCompany("");
          setEmployees([]);
        }
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchProviders();

    return () => {
      controller.abort();
    };
  }, []);

  const handleDelete = async (companyEmail, companyName) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("JWT token not found");
        return;
      }

      await axios.delete(
        `${BASE_URL}/api/services/delete-by-email/${companyEmail}/${encodeURIComponent(
          localStorage.getItem("society") || ""
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setProviders((prev) => prev.filter((p) => p.companyEmail !== companyEmail));
      if (
        selectedCompany &&
        providers.find((p) => p.companyEmail === companyEmail && p.companyName === selectedCompany)
      ) {
        setSelectedCompany("");
        setEmployees([]);
        toast.success("Deleted successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to delete provider", error);
      alert("Failed to delete provider.");
    }
  };

  const handleCopy = (phone, index) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(String(phone)).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    });
  };

  const handleDeleteEmployee = async (emp) => {
    if (!emp || !emp.employeeId) {
      alert("Invalid employee selected");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${emp.employeeId}?`)) return;

    try {
      const token = localStorage.getItem("token");

      await axios.delete(`${BASE_URL}/api/employee/delete-emp-save-archive`, {
        params: {
          employeeId: emp.employeeId,
          societyName: societyName,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Employee deleted and archived successfully.");
      toast.error("Employee deleted and archived successfully.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setEmployees((prev) => prev.filter((e) => e.employeeId !== emp.employeeId));
      setStoredEmployeeImages((prev) => {
        const copy = { ...prev };
        delete copy[emp.employeeId];
        return copy;
      });
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert("Failed to delete employee.");
    }
  };

  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const filteredProviders = providers.filter(p =>
    (p.companyName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.companyEmail || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProviderClick = (provider) => {
    const company = provider.companyName || "";
    setSelectedCompany(company);
    if (company) {
      fetchEmployees(company);
      setViewMode("detail");
    }
  };

  const handleBackToGrid = () => {
    setViewMode("grid");
    setSelectedCompany("");
    setEmployees([]);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${BASE_URL}/api/employee/update-resign-date-by-admin`,
        null,
        {
          params: {
            employeeId: selectedEmployeeId,
            resignDate: newResignDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Updated successfully");
      setShowModal(false);

    } catch (err) {
      toast.error("Failed");
    }
  };

  const handleUpdateEmployee = async () => {
    try {
      const formData = new FormData();

      formData.append("employeeId", selectedEmployee.employeeId);
      formData.append("employeeName", selectedEmployee.employeeName);
      formData.append("designation", selectedEmployee.designation);
      formData.append("email", selectedEmployee.employeeEmail);
      formData.append("phone", selectedEmployee.employeePhone);
      formData.append("address", selectedEmployee.employeeAddress);
      formData.append("companyName", selectedEmployee.companyName);


      if (selectedFile) {
        formData.append("employeeImage", selectedFile);
      }

      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${BASE_URL}/api/employee/updated-employee-by-admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log(res.data);
      const updatedList = employees.map((emp) =>
        emp.employeeId === selectedEmployee.employeeId
          ? {
            ...selectedEmployee,
            ...(selectedFile && {
              imageUrl: URL.createObjectURL(selectedFile),
            }),
          }
          : emp
      );

      setEmployees(updatedList);
      if (selectedFile) {
        setStoredEmployeeImages((prev) => ({
          ...prev,
          [selectedEmployee.employeeId]: URL.createObjectURL(selectedFile),
        }));
      }

      alert("Employee updated successfully!");
      setShowEditModal(false);

    } catch (err) {
      console.error(err);
      alert("Update failed!");
    }
  };

  // const handleUpdateProvider = (updatedProvider) => {
  //   const updatedList = providers.map((p) =>
  //     p.companyEmail === updatedProvider.companyEmail ? updatedProvider : p
  //   );

  //   setProviders(updatedList);
  // };

  const handleUpdateProvider = async (provider) => {
  try {
    const token = localStorage.getItem("token"); // JWT token if your API is protected
    if (!token) {
      toast.error("Token not found, please login again");
      return;
    }

    const response = await axios.post(
      `${BASE_URL}/api/services/updated-services-by-admin`,
      null,
      {
        params: {
          serviceProviderId: provider.serviceProviderId,
          companyName: provider.companyName,
          companyEmail: provider.companyEmail,
          companyPhone: provider.companyPhone,
          companyAddress: provider.companyAddress,
          companyServices: provider.companyServices,
          companyWebsite: provider.companyWebsite,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Provider updated successfully!");
    setProviders((prev) =>
      prev.map((p) =>
        p.serviceProviderId === provider.serviceProviderId ? provider : p
      )
    );

  } catch (error) {
    console.error("Error updating provider:", error);
    toast.error("Failed to update provider. Try again.");
  }
};

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />

      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main className="content-area">
          <SubHeader />

          <div className="premium-provider-container">

            {viewMode === "grid" ? (
              <div className="provider-grid-view">
                <div className="grid-header">
                  <div className="header-title-section">
                    <Building2 size={28} className="header-icon-large" />
                    <div>
                      <h2 className="page-title">Service Providers</h2>
                      <p className="page-subtitle">Manage external service agencies and their employees</p>
                    </div>
                  </div>
                  <div className="header-actions">
                    <div className="search-bar-premium">
                      <input
                        type="text"
                        placeholder="Search providers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <button
                      className="btn-primary-premium"
                      onClick={() => navigate("/add-services")}
                    >
                      <Plus size={18} />
                      <span>Add Provider</span>
                    </button>
                  </div>
                </div>

                <div className="providers-grid">
                  {loadingProviders ? (
                    <div className="grid-status">
                      <div className="loading-spinner"></div>
                      <span>Loading providers...</span>
                    </div>
                  ) : filteredProviders.length === 0 ? (
                    <div className="grid-status">
                      <span>No providers found matching "{searchTerm}"</span>
                    </div>
                  ) : (
                    filteredProviders.map((provider, index) => (
                      <div
                        key={provider.companyEmail || index}
                        className="provider-card-premium"
                        onClick={() => handleProviderClick(provider)}
                      >
                        <div className="card-top">
                          <div className="provider-avatar-large">
                            {provider.companyName ? provider.companyName.charAt(0).toUpperCase() : "P"}
                          </div>
                          <button
                            className="btn-delete-card"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(provider.companyEmail);
                            }}
                            title="Delete Provider"
                          >
                            <Trash2 size={16} />
                          </button>
                         <button
  className="btn-delete-card"
  onClick={(e) => {
    e.stopPropagation();
    setSelectedProvider(provider);
    setIsEditModalOpen(true);
  }}
  title="Edit Provider"
>
  <Edit size={16} />
</button>
                        </div>
                        <div className="card-content">
                          <h3 className="provider-title">{provider.companyName || "Unknown Provider"}</h3>
                          <div className="provider-meta">
                            <span className="meta-item">{provider.companyEmail || "No Email"}</span>
                            <span className="meta-item">{provider.companyPhone || "No Phone"}</span>
                          </div>
                        </div>
                        <div className="card-footer">
                          <span className="view-details-link">View Employees &rarr;</span>
                        </div>
                      </div>
                    ))
                  )}

                </div>
  {isEditModalOpen && selectedProvider && (
  <div className="provider-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
    <div className="provider-modal" onClick={(e) => e.stopPropagation()}>
      <h2>Edit Provider</h2>

      {/* Company Name */}
      <input
        type="text"
        placeholder="Company Name"
        value={selectedProvider.companyName || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyName: e.target.value })
        }
      />

      {/* Company Email */}
      <input
        type="email"
        placeholder="Company Email"
        value={selectedProvider.companyEmail || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyEmail: e.target.value })
        }
      />

      {/* Company Phone */}
      <input
        type="text"
        placeholder="Company Phone"
        value={selectedProvider.companyPhone || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyPhone: e.target.value })
        }
      />

      {/* Company Address */}
      <input
        type="text"
        placeholder="Company Address"
        value={selectedProvider.companyAddress || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyAddress: e.target.value })
        }
      />

      {/* Company Services */}
      <input
        type="text"
        placeholder="Company Services"
        value={selectedProvider.companyServices || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyServices: e.target.value })
        }
      />

      {/* Company Website */}
      <input
        type="text"
        placeholder="Company Website"
        value={selectedProvider.companyWebsite || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, companyWebsite: e.target.value })
        }
      />

      {/* Society Name */}
      <input
        type="text"
        placeholder="Society Name"
        value={selectedProvider.societyName || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, societyName: e.target.value })
        }
      />

      {/* Gate Number */}
      <input
        type="text"
        placeholder="Gate Number"
        value={selectedProvider.gateNumber || ""}
        onChange={(e) =>
          setSelectedProvider({ ...selectedProvider, gateNumber: e.target.value })
        }
      />

      {/* Action Buttons */}
      <div className="modal-actions">
        <button onClick={() => setIsEditModalOpen(false)}>Cancel</button>
        <button
          onClick={() => {
            handleUpdateProvider(selectedProvider);
            setIsEditModalOpen(false);
          }}
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}
              </div>
            ) : (
              <div className="provider-detail-view">
                <div className="detail-header-actions">
                  <button className="btn-back-premium" onClick={handleBackToGrid}>
                    &larr; Back to Providers
                  </button>
                </div>

                <div className="premium-content-card full-width-card">
                  <div className="content-header-premium">
                    <div className="header-title-group">
                      <div className="provider-avatar-small">
                        {selectedCompany ? selectedCompany.charAt(0).toUpperCase() : "P"}
                      </div>
                      <div className="header-text">
                        <h3>{selectedCompany}</h3>
                        <p>Managing employees list</p>
                      </div>
                    </div>
                    <button
                      className="btn-primary-premium"
                      onClick={() => navigate("/add-employee", { state: { companyName: selectedCompany } })}
                    >
                      <Plus size={18} />
                      <span>Add Employee</span>
                    </button>
                  </div>

                  <div className="premium-table-wrapper">
                    <table className="premium-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Address</th>
                          <th>Designation</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingEmployees ? (
                          <tr>
                            <td colSpan={6} className="table-status">
                              <div className="loading-spinner"></div>
                              <span>Loading employees...</span>
                            </td>
                          </tr>
                        ) : employees.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="table-status">
                              <span>No employees found for this provider.</span>
                            </td>
                          </tr>
                        ) : (
                          employees.map((emp, index) => (
                            <tr key={emp.employeeId ?? index}>
                              <td>
                                <div className="employee-profile">
                                  {storedEmployeeImages[emp.employeeId] ? (
                                    <img
                                      src={storedEmployeeImages[emp.employeeId]}
                                      alt="emp"
                                      className="employee-avatar"
                                    />
                                  ) : (
                                    <div className="employee-avatar-placeholder">
                                      {emp.employeeName ? emp.employeeName.charAt(0).toUpperCase() : "E"}
                                    </div>
                                  )}
                                  <span className="employee-name">{emp.employeeName || "Unknown"}</span>
                                </div>
                              </td>
                              <td>
                                <div className="contact-cell">
                                  <span>{emp.employeePhone || "—"}</span>
                                  <button
                                    className="btn-copy"
                                    onClick={() => handleCopy(emp.employeePhone, index)}
                                    title="Copy Phone"
                                  >
                                    <Copy size={12} />
                                  </button>
                                  {copiedIndex === index && <span className="copied-badge">Copied!</span>}
                                </div>
                              </td>
                              <td className="text-muted">{emp.employeeEmail || "—"}</td>
                              <td className="text-muted truncate-cell" title={emp.employeeAddress}>
                                {emp.employeeAddress || "—"}
                              </td>
                              <td>
                                <span className="designation-badge">{emp.designation || "Staff"}</span>
                              </td>
                              <td className="text-center">
                                <button
                                  onClick={() => handleDeleteEmployee(emp)}
                                  className="btn-action-delete"
                                  title="Delete Employee"
                                >
                                  <Trash2 size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedEmployeeId(emp.employeeId);
                                    setNewResignDate("");
                                    setShowModal(true);
                                  }}
                                  style={{ background: "none", border: "none", cursor: "pointer" }}
                                >
                                  <FaCalendarAlt />
                                </button>
                                <button
                                  onClick={() => {
                                    console.log("Edit clicked", emp); // 👈 debug
                                    setSelectedEmployee(emp);
                                    setShowEditModal(true);
                                  }}
                                  style={{ background: "none", border: "none", cursor: "pointer" }}
                                >
                                  <FaEdit />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {showModal && (
              <div style={styles.overlay}>
                <div style={styles.modal}>

                  {/* Header */}
                  <div style={styles.header}>
                    <h3 style={styles.title}>Edit Resign Date</h3>
                  </div>

                  {/* Body */}
                  <div style={styles.body}>
                    <label style={styles.label}>Select Date</label>

                    <input
                      type="date"
                      value={newResignDate || ""}
                      onChange={(e) => setNewResignDate(e.target.value)}
                      style={styles.input}
                    />
                  </div>

                  {/* Footer */}
                  <div style={styles.footer}>
                    <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                      Cancel
                    </button>

                    <button style={styles.saveBtn} onClick={handleSave}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showEditModal && selectedEmployee && (
              <div className="modal-overlay">
                <div className="modal-content">

                  {/* HEADER */}
                  <div className="modal-header">
                    <h3>Edit Employee</h3>
                    <button className="close-icon" onClick={() => setShowEditModal(false)}>×</button>
                  </div>

                  {/* PROFILE SECTION */}
                  <div className="profile-section">
                    <div className="profile-image">
                      {previewImage ? (
                        <img src={previewImage} alt="preview" />
                      ) : storedEmployeeImages?.[selectedEmployee?.employeeId] ? (
                        <img src={storedEmployeeImages[selectedEmployee.employeeId]} alt="employee" />
                      ) : (
                        <div className="placeholder">
                          {selectedEmployee?.employeeName?.charAt(0)?.toUpperCase() || "E"}
                        </div>
                      )}
                    </div>

                    <div className="profile-info">
                      <h4>{selectedEmployee?.employeeName || "Employee"}</h4>
                      <p>{selectedEmployee?.designation || "Designation"}</p>
                    </div>
                  </div>

                  {/* FILE */}
                  <input type="file" onChange={handleImageChange} className="file-input" />

                  {/* FORM */}
                  <div className="form-grid">

                    <div className="input-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={selectedEmployee?.employeeName || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, employeeName: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>Designation</label>
                      <input
                        type="text"
                        value={selectedEmployee?.designation || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, designation: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={selectedEmployee?.employeeEmail || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, employeeEmail: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={selectedEmployee?.employeePhone || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, employeePhone: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>Address</label>
                      <input
                        type="text"
                        value={selectedEmployee?.employeeAddress || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, employeeAddress: e.target.value })
                        }
                      />
                    </div>

                    <div className="input-group">
                      <label>Company</label>
                      <input
                        type="text"
                        value={selectedEmployee?.companyName || ""}
                        onChange={(e) =>
                          setSelectedEmployee({ ...selectedEmployee, companyName: e.target.value })
                        }
                      />
                    </div>

                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer">

                    <button className="btn-update" onClick={handleUpdateEmployee}>
                      Update
                    </button>
                  </div>

                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}; const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)", // 🔥 modern blur
  },

  modal: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "350px",
    padding: "0",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    animation: "fadeIn 0.3s ease",
    overflow: "hidden",
  },

  header: {
    padding: "15px",
    borderBottom: "1px solid #eee",
    textAlign: "center",
    background: "#f8f9fa",
  },

  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },

  body: {
    padding: "20px",
    textAlign: "left",
  },

  label: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "5px",
    display: "block",
  },

  input: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
    marginTop: "5px",
  },

  footer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px",
    borderTop: "1px solid #eee",
  },

  saveBtn: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
  },

  cancelBtn: {
    background: "#e0e0e0",
    color: "#333",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },

  modal: {
    background: "#fff",
    padding: "24px",
    borderRadius: "12px",
    width: "380px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  header: {
    borderBottom: "1px solid #eee",
    paddingBottom: "10px",
  },

  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "600",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },

  label: {
    fontSize: "13px",
    marginBottom: "4px",
    color: "#555",
  },

  input: {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
  },

  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "10px",
  },

  saveBtn: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
  },

  cancelBtn: {
    background: "#e0e0e0",
    color: "#333",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
  },


};

export default ServiceProvider;
